import React from 'react';
import { FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Input } from 'antd';
import { UseFormReturn } from 'react-hook-form';
import { PipelineFieldRow, PIPELINE_INPUT_CLASS } from '@/components/form/pipelineFieldRow';
import { usePipelineForm } from '@/components/form/PipelineFormContext';
import { cn } from '@/lib/utils';

interface TextareaFieldProps {
  form: UseFormReturn<any>;
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  rows?: number;
  variant?: 'default' | 'pipeline';
}

export function TextareaField({
  form,
  name,
  label,
  placeholder,
  required = false,
  className,
  rows = 4,
  variant,
}: TextareaFieldProps) {
  const { isPipeline, variant: contextVariant } = usePipelineForm();
  const resolvedVariant = variant ?? contextVariant;
  const usePipeline = resolvedVariant === 'pipeline';

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const errorMessage = form.formState.errors[name]?.message as string | undefined;

        if (usePipeline) {
          return (
            <FormItem className={cn('flex flex-col p-0 space-y-0', className)}>
              <PipelineFieldRow label={label} required={required} error={errorMessage}>
                <Input.TextArea
                  {...field}
                  value={field.value ?? ''}
                  placeholder={placeholder}
                  variant="borderless"
                  className={cn(PIPELINE_INPUT_CLASS, '!min-h-[72px] !resize-none')}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  status={errorMessage ? 'error' : undefined}
                />
              </PipelineFieldRow>
            </FormItem>
          );
        }

        return (
          <FormItem className={className}>
            <label className="text-base font-medium">
              {label}
              {required && '*'}
            </label>
            <FormControl>
              <Textarea
                {...field}
                placeholder={placeholder}
                className="min-h-[100px] resize-none"
                rows={rows}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export default TextareaField;
